import { Alert, Anchor, Badge, Button, Code, Group, Loader, Stack, Table, Text, Title } from "@mantine/core";
import { useCanGoBack, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useId, type ReactNode } from "react";

import { useGetStaffJobTrace } from "../../api/generated/endpoints/staff/staff";
import type { TraceJob, TraceRunStep, UsageTrace } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { formatCount, formatDateTime } from "../../ui/format";
import { TraceInputs, TraceOutputs } from "./TraceFiles";
import { TraceRequests, TraceWebhooks } from "./TraceRequests";
import {
  Facts,
  JobCost,
  JobStatusBadge,
  StatusBadge,
  TraceLink,
  formatDuration,
  typeName,
} from "./usage-format";
import classes from "./usage.module.css";

// Every read writes an audit row, so a trace is read each time it is opened and at no other
// time. No screen shows a cached one: it leaves the cache as soon as none shows it (`gcTime: 0`),
// so coming back to it, or a parent's trace after its step's, reads it again; and while it shows,
// nothing reads it behind the reader's back: not focus, a reconnect, its age or a retry.
// "Re-open" reads it again on purpose.
const ONCE = {
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

const fileTime = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });

/** A job by its model and its type's name: "Seedream 4.0 · Tweak". */
const jobName = (job: { type: string; model: TraceJob["model"] }) =>
  `${job.model?.name ?? job.model?.id ?? "No model"} · ${typeName(job.type) ?? job.type}`;

/** A run's step by its name, tile or shot, and attempt: "start_frame (tile 0), attempt 1". */
const stepName = (step: TraceRunStep) => {
  const place = step.tile === null ? (step.shot === null ? "" : ` (shot ${step.shot})`) : ` (tile ${step.tile})`;
  return `"${step.name}"${place}, attempt ${step.attempt}${step.unused ? ", let go of" : ""}`;
};

/** A titled part of the trace, a region named by its title. */
function Part({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <Stack component="section" gap="sm" aria-labelledby={id}>
      <Title id={id} order={3} size="h4">
        {title}
      </Title>
      {children}
    </Stack>
  );
}

/**
 * One generation down to each vendor request it made (spec usage-analytics 3): its header, its
 * inputs and outputs with their files, its requests with their bodies and webhooks, its credits,
 * and its parent and steps, each opening its own trace. Opening it is an audited read, and only
 * the API answers it: another team's job, or none, is its not-found.
 */
export function StaffTraceScreen() {
  const { teamId, jobId } = useParams({ from: "/signed-in/staff/teams/$teamId/jobs/$jobId" });
  const trace = useGetStaffJobTrace(teamId, jobId, { query: ONCE });
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();
  const back = () => (canGoBack ? router.history.back() : void navigate({ to: "/staff/usage" }));
  return (
    <Stack gap="lg">
      <Anchor component="button" type="button" onClick={back} ta="left">
        ← Back
      </Anchor>
      {trace.isPending ? <Loader size="sm" aria-label="Loading the trace" /> : null}
      {trace.isError ? <ErrorAlert error={trace.error} /> : null}
      {trace.data === undefined || trace.isError ? null : (
        <Trace
          trace={trace.data}
          reopening={trace.isFetching}
          onReopen={() => void trace.refetch()}
        />
      )}
    </Stack>
  );
}

function Trace(props: { trace: UsageTrace; reopening: boolean; onReopen: () => void }) {
  const { trace, reopening, onReopen } = props;
  const files = new Map(trace.files.map((file) => [file.storage_key, file]));
  const fileOf = (key: string | null) => (key === null ? undefined : files.get(key));
  const team = trace.team_name ?? trace.team_id;
  return (
    <Stack gap="lg">
      <Alert color="indigo" variant="light" icon={<Info size={18} />}>
        <Text size="sm">
          <b>Recorded in the audit log.</b> Opening this trace wrote one row, “Read a generation's
          trace”, with your name, {team} and this job.
        </Text>
      </Alert>
      <Stack gap="sm">
        <Group gap="sm">
          <Title order={2} size="h3">
            {jobName(trace)}
          </Title>
          <JobStatusBadge status={trace.status} />
          {trace.deleted ? (
            <Badge color="gray" variant="light">
              Deleted by its maker
            </Badge>
          ) : null}
          {trace.parent === null ? null : (
            <Badge color="gray" variant="light">
              A step of a run
            </Badge>
          )}
        </Group>
        <Header trace={trace} team={team} />
      </Stack>
      <Group gap="sm">
        <Text size="sm" c="var(--cai-color-text-muted)">
          {trace.files.length === 0
            ? "None of its files is in the bucket any more."
            : `Each file opens through a link to that one file, valid until ${fileTime.format(new Date(trace.files_expire_at))}.`}{" "}
          Re-opening reads the trace again, with new links, and writes another audit row.
        </Text>
        <Button variant="light" size="compact-sm" loading={reopening} onClick={onReopen}>
          Re-open
        </Button>
      </Group>
      <Part title="Inputs">
        <TraceInputs inputs={trace.inputs} fileOf={fileOf} />
      </Part>
      <Part title="Outputs">
        <TraceOutputs outputs={trace.outputs} fileOf={fileOf} />
      </Part>
      <Part title="Vendor requests">
        <TraceRequests requests={trace.requests} cost={trace.cost} />
        <Title order={4} size="h5">
          Webhook deliveries
        </Title>
        <TraceWebhooks webhooks={trace.webhooks} />
      </Part>
      <Part title="Credits">
        <Credits trace={trace} />
      </Part>
      <Part title="Parent and steps">
        <Links trace={trace} />
      </Part>
    </Stack>
  );
}

function Header({ trace, team }: { trace: UsageTrace; team: string }) {
  const { user, model, finished_at: finished } = trace;
  // From its start, as Generations shows it: a synchronous vendor (Gemini) answers within the
  // start's call, before the job's `started_at`, which would leave that call out of the time.
  const started = trace.created_at;
  const took = finished === null ? null : formatDuration(Date.parse(finished) - Date.parse(started));
  const items: [string, ReactNode][] = [
    [
      "Team · brand",
      <>
        {team}
        {trace.brand_id === null ? null : (
          <Text component="span" display="block" size="xs" ff="monospace">
            {trace.brand_id}
          </Text>
        )}
      </>,
    ],
    [
      "User",
      user === null ? (
        "No user"
      ) : (
        <>
          {user.name ?? user.email}
          {user.name === null ? null : (
            <Text component="span" display="block" size="xs" c="var(--cai-color-text-muted)">
              {user.email}
            </Text>
          )}
          {user.deleted_at === null ? null : (
            <Badge size="xs" color="gray" variant="light">
              Deleted
            </Badge>
          )}
        </>
      ),
    ],
    ["Started", formatDateTime(started)],
    ["Finished", finished === null ? "not yet" : `${formatDateTime(finished)} (${took})`],
    ["Job", <Code key="job">{trace.id}</Code>],
    ["Type", <Code key="type">{trace.type}</Code>],
    [
      "Model",
      model === null ? (
        "No model"
      ) : (
        <>
          {`${model.name ?? model.id} · ${model.vendor ?? "vendor unknown"}`}
          {model.vendor_model === null ? null : (
            <Text component="span" display="block" size="xs" ff="monospace">
              {model.vendor_model}
            </Text>
          )}
        </>
      ),
    ],
    [
      "Catalog · price version",
      `${trace.catalog_version ?? "—"} · ${trace.price_version_id ?? "—"}`,
    ],
    ["Attempt", String(trace.attempt)],
  ];
  if (trace.error_code !== null) {
    items.push(["Error", `${trace.error_code}${trace.error_message === null ? "" : `: ${trace.error_message}`}`]);
  }
  return <Facts items={items} />;
}

/** The job's billing line: its hold, charge, debt, unpriced SKUs, refund and cost as billing has them. */
function Credits({ trace }: { trace: UsageTrace }) {
  const { line } = trace;
  if (line === null) return <Text size="sm">No billing line: none was written for this job.</Text>;
  return (
    <Facts
      items={[
        ["Line", <StatusBadge key="status" status={line.status} />],
        ["Held", formatCount(line.held)],
        ["Charged", formatCount(line.charged)],
        ["Debt", formatCount(line.debt)],
        ["Unpriced SKUs", line.unpriced.length === 0 ? "none" : line.unpriced.join(", ")],
        [
          "Refunded",
          line.refunded_at === null
            ? formatCount(line.refunded)
            : `${formatCount(line.refunded)}, ${formatDateTime(line.refunded_at)}`,
        ],
        ["Net", formatCount(line.charged - line.refunded)],
        ["Settled", line.settled_at === null ? "not yet" : formatDateTime(line.settled_at)],
        [
          "Vendor cost",
          <JobCost
            key="cost"
            usd={line.vendor_cost_usd}
            complete={line.vendor_cost_complete}
            open={line.status === "open"}
          />,
        ],
        ["Price version", line.price_version_id ?? "—"],
      ]}
    />
  );
}

/** The run the job is a step of, and the steps its own run started, each opening its trace. */
function Links({ trace }: { trace: UsageTrace }) {
  const { parent, step, steps, team_id: teamId } = trace;
  if (parent === null && steps.length === 0) {
    return (
      <Text size="sm" c="var(--cai-color-text-muted)">
        No parent and no steps: a generation started on its own.
      </Text>
    );
  }
  return (
    <Stack gap="sm">
      {parent === null ? null : (
        <Text size="sm">
          {step === null ? "A step of " : `Step ${stepName(step)}, of `}
          <TraceLink teamId={teamId} jobId={parent.id}>
            {jobName(parent)}, started {formatDateTime(parent.created_at)}
          </TraceLink>
          .
        </Text>
      )}
      {steps.length === 0 ? null : (
        <Table.ScrollContainer minWidth={760}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Step</Table.Th>
                <Table.Th>Job</Table.Th>
                <Table.Th className={classes.status}>Status</Table.Th>
                <Table.Th className={classes.num}>Credits</Table.Th>
                <Table.Th className={classes.num}>Vendor cost</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {steps.map((each) => {
                const { job } = each;
                return (
                  <Table.Tr key={job.id}>
                    <Table.Td>{stepName(each)}</Table.Td>
                    <Table.Td>{jobName(job)}</Table.Td>
                    <Table.Td>
                      <JobStatusBadge status={job.status} />
                    </Table.Td>
                    <Table.Td className={classes.num}>
                      {job.line === null ? "—" : formatCount(job.line.charged - job.line.refunded)}
                    </Table.Td>
                    <Table.Td className={classes.num}>
                      {job.line === null ? (
                        "—"
                      ) : (
                        <JobCost
                          usd={job.line.vendor_cost_usd}
                          complete={job.line.vendor_cost_complete}
                          open={job.line.status === "open"}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <TraceLink teamId={teamId} jobId={job.id}>
                        Open its trace
                      </TraceLink>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
